import { IDropdownOption } from '@fluentui/react';
import { Button, buttonStylesGhost, buttonStylesPrimary, Dropdown, Panel, TextField, useTheme } from '@h2oai/ui-kit';
import React, { useCallback, useEffect, useState } from 'react';

import { GetCustomerManagedGroupsRequest } from '../../../../../admin-center/gen/custom_group_management/custom_group_management_pb';
import { AddMemberRequest } from '../../../../../admin-center/gen/user_management/user_management_pb';
import { useAdminCenterService } from '../../../../../admin-center/hooks';
import { useUser } from '../../../../../utils/hooks';
import { buildUserContext } from '../../../common/utils';
import { UserRoleOptionKey } from '../../../constants';
import { sidePanelBtnStyle, sidePanelCancelBtnStyle } from '../UserManagement.styles';

interface IPPanelProps {
  isOpen: boolean;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
  onSubmitSuccess?: () => Promise<void>;
  setAlertNotification: (isSuccess: boolean, message: string) => void;
  envId: string | null;
}

export default function UMSidePanel({ isOpen, setOpen, onSubmitSuccess, setAlertNotification, envId }: IPPanelProps) {
  const service = useAdminCenterService();
  const theme = useTheme();
  const user = useUser();
  const [responseMessage, setResponseMessage] = useState<string | null>(null);
  const [firstNameValue, setFirstNameValue] = useState('');
  const [selectedRoleKeys, setSelectedRoleKeys] = useState<string[]>([UserRoleOptionKey.user]);
  const [isRequestLoading, setIsRequestLoading] = useState<boolean>(false);
  const [customGroups, setCustomGroups] = useState<string[]>([]);

  useEffect(() => {
    const fetchCustomGroups = async () => {
      try {
        const req: GetCustomerManagedGroupsRequest = {};
        const response = await service.getCustomerManagedGroupsRequest(req);
        setCustomGroups(response.userGroups || []);
      } catch (error) {
        console.error('Error fetching custom groups:', error);
      }
    };
    if (isOpen) {
      fetchCustomGroups();
    }
  }, [isOpen, service, user]);

  const handleFirstNameChange = (
    _event: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>,
    newValue?: string
  ) => {
    setFirstNameValue(newValue || '');
  };

  const [lastNameValue, setLastNameValue] = useState('');
  const handleLastNameChange = (_event: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>, newValue?: string) => {
    setLastNameValue(newValue || '');
  };

  const [emailValue, setEmailValue] = useState('');
  const handleEmailChange = (_event: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>, newValue?: string) => {
    setEmailValue(newValue || '');
  };

  const groupOptions = [
    { key: UserRoleOptionKey.user, text: 'Users' },
    { key: UserRoleOptionKey.admin, text: 'Admins' },
    ...customGroups.map((group) => ({ key: group, text: group })),
  ];
  const onRoleChange = (_event: React.FormEvent<HTMLDivElement>, option?: IDropdownOption, _index?: number) => {
    if (!option) return;

    setSelectedRoleKeys((prevKeys: string[]) => {
      if (option.selected) {
        return [...prevKeys, option.key as string];
      } else {
        return prevKeys.filter((k) => k !== option.key);
      }
    });
  };

  const dismissPanel = useCallback(() => {
    setTimeout(() => {
      setFirstNameValue('');
      setLastNameValue('');
      setEmailValue('');
      setSelectedRoleKeys([UserRoleOptionKey.user]);
      setOpen(false);
    }, 500);
  }, [setOpen, setFirstNameValue, setLastNameValue, setEmailValue, setSelectedRoleKeys]);
  const isFormValid = useCallback(() => {
    return [firstNameValue, lastNameValue, emailValue, selectedRoleKeys.length > 0].every(Boolean);
  }, [firstNameValue, lastNameValue, emailValue, selectedRoleKeys]);

  function clearForm() {
    setFirstNameValue('');
    setLastNameValue('');
    setEmailValue('');
    setSelectedRoleKeys([UserRoleOptionKey.user]);
  }

  const handleSubmitClick = useCallback(async () => {
    if (!isFormValid()) return;

    if (!envId && selectedRoleKeys.some((key) => key === UserRoleOptionKey.admin || key === UserRoleOptionKey.user)) {
      setAlertNotification(false, '❌ Error: Environment ID not available. Please refresh the page.');
      return;
    }

    try {
      const userContext = await buildUserContext(user);
      const groupNames = selectedRoleKeys.map((key) => {
        if (key === UserRoleOptionKey.admin) return `hadc_${envId}_admin`;
        if (key === UserRoleOptionKey.user) return `hadc_${envId}_user`;
        return key; // custom group names are used as-is
      });

      const request: AddMemberRequest = {
        members: [
          {
            firstName: firstNameValue ?? '',
            lastName: lastNameValue ?? '',
            email: emailValue ?? '',
          },
        ],
        groupNames: groupNames,
        modifiedBy: user?.name ?? '',
        timestamp: Date.now() / 1000,
        userContext: userContext,
      };
      const response = await service.addMembers(request);
      if (response.statusUpdate) {
        const statusMessage = response.statusUpdate;
        const match = statusMessage.match(/Failed to create users?: (.+)/);
        const failedEmails = match && match[1] ? match[1].split(',').map((email) => email.trim()) : [];

        if (failedEmails.length > 0) {
          setResponseMessage(statusMessage);
        } else {
          setAlertNotification(true, 'User was added successfully');
          if (onSubmitSuccess) {
            await onSubmitSuccess();
          }
        }
      } else {
        setAlertNotification(false, 'Failed to update member group');
      }
      clearForm();
    } catch (error) {
      setAlertNotification(false, `Network error: ${error}`);
    } finally {
      setIsRequestLoading(false);
    }
  }, [firstNameValue, lastNameValue, emailValue, isFormValid, onSubmitSuccess, isRequestLoading, setAlertNotification]);

  const onRenderFooterContent = useCallback(
    () => (
      <>
        <div style={sidePanelBtnStyle}>
          <Button styles={buttonStylesGhost} text="Cancel" onClick={dismissPanel} style={sidePanelCancelBtnStyle} />
          <Button
            styles={buttonStylesPrimary}
            text={isRequestLoading ? '...' : 'Add Member'}
            onClick={() => {
              setIsRequestLoading(true);
              handleSubmitClick();
            }}
            disabled={!isFormValid() || isRequestLoading}
          />
        </div>
      </>
    ),
    [dismissPanel, handleSubmitClick, isFormValid]
  );

  return (
    <Panel
      isLightDismiss
      isOpen={isOpen}
      onDismiss={dismissPanel}
      closeButtonAriaLabel="Close"
      isFooterAtBottom={true}
      onRenderFooterContent={onRenderFooterContent}
      customWidth="500px"
      type={7}
    >
      <h2>
        <strong>Add Member</strong>
      </h2>
      <TextField
        label="First Name"
        placeholder="First name here"
        value={firstNameValue}
        onChange={handleFirstNameChange}
        required={true}
      />
      <TextField
        label="Last Name"
        placeholder="Last name here"
        value={lastNameValue}
        onChange={handleLastNameChange}
        required={true}
      />
      <TextField
        label="Email"
        placeholder="Email here"
        value={emailValue}
        onChange={handleEmailChange}
        required={true}
      />
      <Dropdown
        label="Groups"
        placeholder="Select groups"
        multiSelect
        options={groupOptions}
        selectedKeys={selectedRoleKeys}
        onChange={onRoleChange}
        required={true}
      />
      {responseMessage && (
        <p
          style={{
            marginTop: 10,
            color: responseMessage.toLowerCase().includes('failed') ? theme.palette?.red300 : theme.palette?.green300,
          }}
        >
          {responseMessage}
        </p>
      )}
    </Panel>
  );
}
